import React, { createContext, useContext, useEffect, useState } from 'react';
import { NamiLiquidMetalLogo } from './NamiLiquidMetalLogo';

const APP_MOUNT_DELAY_MS = 120;
const SPLASH_HOLD_MS = 2600;
const SPLASH_EXIT_MS = 520;

const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';

type SplashPhase = 'splash' | 'exiting' | 'done';

const SplashPhaseContext = createContext<SplashPhase>('splash');

/** Splash phase so login can time entrance animations when the app becomes visible. */
export function useSplashPhase(): SplashPhase {
  return useContext(SplashPhaseContext);
}

interface SplashGateProps {
  children: React.ReactNode;
}

/**
 * Liquid-metal splash, then crossfade into the app (splash exits with opacity transition).
 */
export const SplashGate: React.FC<SplashGateProps> = ({ children }) => {
  const [mountApp, setMountApp] = useState(false);
  const [phase, setPhase] = useState<SplashPhase>('splash');

  useEffect(() => {
    const t = window.setTimeout(() => setMountApp(true), APP_MOUNT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setPhase('exiting'), APP_MOUNT_DELAY_MS + SPLASH_HOLD_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase !== 'exiting') return;
    const t = window.setTimeout(() => setPhase('done'), SPLASH_EXIT_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  const showSplashLayer = phase !== 'done';

  return (
    <SplashPhaseContext.Provider value={phase}>
      <div className="relative min-h-dvh min-h-screen bg-slate-950">
        {showSplashLayer && (
          <div
            className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-slate-950"
            style={{
              opacity: phase === 'exiting' ? 0 : 1,
              transition: `opacity ${SPLASH_EXIT_MS}ms ${ease}`,
              pointerEvents: phase === 'exiting' ? 'none' : 'auto',
            }}
            aria-hidden={phase !== 'splash'}
          >
            <div className="relative z-10 w-full px-4 sm:px-8">
              <NamiLiquidMetalLogo variant="splash" />
            </div>
          </div>
        )}

        {mountApp && (
          <div
            className="relative z-10 min-h-dvh min-h-screen"
            style={{
              opacity: phase === 'splash' ? 0 : 1,
              transition: `opacity ${SPLASH_EXIT_MS}ms ${ease}`,
              pointerEvents: phase === 'splash' ? 'none' : 'auto',
            }}
            aria-hidden={phase === 'splash'}
          >
            {children}
          </div>
        )}
      </div>
    </SplashPhaseContext.Provider>
  );
};
